import { trackSignUp } from "@/lib/analytics";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { useAuthState } from "@/hooks/useAuthState";
import {
  clearFreshLogin,
  hasFreshLogin,
  markFreshLogin,
} from "@/lib/fresh-login-signal";
import { getPanelUrl } from "@/lib/panel-url";
import { shouldRunPostAuthRedirect } from "@/lib/post-auth-redirect-policy";
import { getSupabase } from "@/lib/supabase-lazy";
import {
  consumePostAuthIntent,
  decodeIntentFromUrl,
  intentToPath,
  setPostAuthIntent,
  type PostAuthIntent,
} from "@/lib/post-auth-intent";

// Waitlist gate: after any SIGNED_IN event, check if the just-authenticated
// user is flagged as waitlisted in their profile. If so — and they are not an
// admin — sign them out immediately and inform them by toast. This closes the
// gap left by Google One Tap and the Google OAuth button, which would
// otherwise let a brand-new user land in the panel bypassing the waitlist.
let waitlistGateInFlight = false;
export async function enforceWaitlistGate(userId: string, email: string | null | undefined) {
  if (waitlistGateInFlight) return false;
  waitlistGateInFlight = true;
  try {
    // Nota: ya NO existe bypass por intención de pago. Durante la Beta Privada
    // también los registros de Club Pro / Mentoring / add-ons quedan en lista
    // de espera; solo pasan admins y usuarios ya invitados.

    // Admin bypass.
    const supabase = await getSupabase();
    const [{ data: adminFlag }, { data: roFlag }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: userId, _role: "admin" as never }),
      supabase.rpc("has_role", { _user_id: userId, _role: "admin_lectura" as never }),
    ]);
    if (adminFlag === true || roFlag === true) return false;

    // Colaboradores externos (portal /colaboradores) no son clientes: nunca pasan por la lista de espera.
    const { data: authUser } = await supabase.auth.getUser();
    if (authUser.user?.user_metadata?.["partner"] === true) return false;
    const { data: partnerRow } = await supabase.from("expert_providers").select("id").eq("user_id", userId).maybeSingle();
    if (partnerRow) return false;

    const { data: profile } = await supabase
      .from("profiles")
      .select("waitlisted, preferred_language, first_name, last_name")
      .eq("user_id", userId)
      .maybeSingle();

    if (!profile?.waitlisted) return false;

    // Deja constancia del servicio que quería (Google no puede enviarlo en
    // los metadatos del registro).
    try {
      const { joinBetaWaitlist, pendingCheckoutPlan } = await import("@/lib/betaGate");
      const pending = pendingCheckoutPlan();
      // El plan marcado en el pop-up (o «Sin plan» si no marcó nada) manda sobre el CTA previo.
      let choice: string | null = null;
      try { choice = localStorage.getItem("rl_wl_plan_choice"); localStorage.removeItem("rl_wl_plan_choice"); } catch { /* ignore */ }
      const keep = pending.plan === "mentoring" || pending.plan.startsWith("addon:");
      const plan = (!keep && choice ? choice : pending.plan) as typeof pending.plan;
      const interval = plan === pending.plan ? pending.interval : null;
      if (email) {
        await joinBetaWaitlist({
          email,
          firstName: (profile as { first_name?: string | null }).first_name ?? null,
          lastName: (profile as { last_name?: string | null }).last_name ?? null,
          plan,
          interval,
          source: (await import("@/lib/captureChannel")).waitlistSourceFor("google"),
        });
      }
    } catch { /* non-blocking */ }

    const lang = (profile as { preferred_language?: string } | null)?.preferred_language === "en" ? "en" : "es";
    const message = lang === "es"
      ? "Estás en la lista de espera. Te avisaremos por email en cuanto tu turno esté listo. 🍋"
      : "You're on the waiting list. We'll email you the moment your turn is ready. 🍋";
    const { toast } = await import("sonner");
    toast.info(message, { duration: 8000 });

    await supabase.auth.signOut();
    // Guarantee we don't route to dashboard afterwards.
    clearFreshLogin();
    return true;
  } finally {
    waitlistGateInFlight = false;
  }
}

const readAuthErrorFromUrl = (): string | null => {
  const query = new URLSearchParams(window.location.search);
  const hash = window.location.hash.startsWith("#")
    ? new URLSearchParams(window.location.hash.slice(1))
    : new URLSearchParams();

  return (
    query.get("error_description") ||
    hash.get("error_description") ||
    query.get("error") ||
    hash.get("error")
  );
};

const cleanAuthErrorFromUrl = (): void => {
  const params = new URLSearchParams(window.location.search);
  ["error", "error_description", "error_code", "state", "code"].forEach((key) => {
    params.delete(key);
  });
  const qs = params.toString();
  window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
};

// Module-level flag: set only by an explicit sign-in flow or when this page load
// contains an OAuth-return signal. Auth can emit SIGNED_IN again while restoring
// an existing session, so that event must never set this flag by itself.
/**
 * Marca un login iniciado explícitamente por el usuario desde un flujo que no
 * necesariamente vuelve con señales OAuth en la URL (email, One Tap o popup).
 */
export { markFreshLogin };


// Detect OAuth-return signals as soon as the module loads, BEFORE effect 1
// cleans the URL. Runs once per page load.
if (typeof window !== "undefined") {
  try {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || "";
    if (
      params.get("login") === "google" ||
      params.get("intent") ||
      params.has("code") ||
      hash.includes("access_token")
    ) {
      markFreshLogin();
    }
  } catch {
    /* ignore */
  }

}

/**
 * Global post-auth router. Mounted once in <App />.
 */
export function usePostAuthRedirect(): void {
  const { isReady, isAuthenticated } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const initialOAuthReturnRef = useRef<boolean>(hasFreshLogin());

  useEffect(() => {
    if (typeof window === "undefined") return;
    let active = true;
    let subscribed = false;
    let unsubscribe: (() => void) | undefined;

    const subscribe = () => {
      if (subscribed) return;
      subscribed = true;
      void getSupabase().then((supabase) => {
        if (!active) return;
        const { data } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === "SIGNED_OUT") {
            void import("@/lib/captureChannel").then((m) => m.clearConsumedChannel()).catch(() => {});
            return;
          }
          if (event !== "SIGNED_IN") return;
          const user = session?.user;
          if (user) {
            // Alta desde una vía de captación (email, Google o One Tap): una sola vez.
            void import("@/lib/captureChannel").then((m) => m.consumeChannelSignup(user)).catch(() => {});
            void enforceWaitlistGate(user.id, user.email).then((kicked) => {
              try {
                if (kicked) return;
                const provider = (user.app_metadata?.['provider'] as string) || "unknown";
                const isNew = user.created_at && Date.now() - new Date(user.created_at).getTime() < 10 * 60 * 1000;
                if (provider !== "email" && isNew) trackSignUp(user.id, provider);
              } catch { /* noop */ }
            });
          }
        });
        unsubscribe = () => data.subscription.unsubscribe();
      });
    };


    const isPublicHome = window.location.pathname === "/" || window.location.pathname === "/en";
    const events = ["pointerdown", "keydown"] as const;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const hasIdle = typeof w.requestIdleCallback === "function";
    let idleId: number | undefined;
    if (hasFreshLogin() || !isPublicHome) subscribe();
    else {
      // En la portada el listener espera a la primera interacción real: Google
      // One Tap tampoco se monta antes de eso, así que no hay SIGNED_IN que
      // observar y el SDK de backend queda fuera de la carga inicial.
      events.forEach((event) => window.addEventListener(event, subscribe, { once: true, passive: true }));
    }

    return () => {
      active = false;
      if (idleId !== undefined) {
        if (hasIdle) w.cancelIdleCallback?.(idleId);
        else window.clearTimeout(idleId);
      }
      events.forEach((event) => window.removeEventListener(event, subscribe));
      unsubscribe?.();
    };
  }, []);

  // País detectado en el navegador → perfil (solo si aún no tiene país).
  useEffect(() => {
    if (!isReady || !isAuthenticated) return;
    void import("@/lib/geo").then(({ syncProfileCountry }) => syncProfileCountry());
  }, [isReady, isAuthenticated]);

  // 1. Ingest ?intent=... query. Persist locally, then clean the URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isReady) return;

    const authError = readAuthErrorFromUrl();
    if (authError) {
      if (window.location.pathname === "/auth/error") return;
      cleanAuthErrorFromUrl();
      navigate(`/auth/error?error_description=${encodeURIComponent(authError)}`, { replace: true });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("intent");
    if (encoded) {
      const intent = decodeIntentFromUrl(encoded);
      if (intent) setPostAuthIntent(intent);
    }
    const loginProvider = params.get("login");
    const hadIntent = !!encoded;
    const hadLogin = loginProvider === "google";

    if (hadIntent || hadLogin) {
      params.delete("intent");
      params.delete("login");
      const qs = params.toString();
      const newUrl =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }

    if (hadLogin && !isAuthenticated) {
      void import("@/lib/social-auth").then(({ socialLogin }) => {
        void socialLogin("google");
      });
    }
  }, [isReady, isAuthenticated, navigate]);

  // 2. Strip OAuth hash tokens (#access_token=…&…) once the session is set.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isReady || !isAuthenticated) return;
    if (window.location.hash.includes("access_token") || window.location.hash === "#") {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
  }, [isReady, isAuthenticated]);

  // 3. Redirect after login. Two cases:
  //    (a) Explicit fresh login or OAuth return → ALWAYS route to
  //        panel/dashboard, regardless of host. This is what the user wants:
  //        after login on any device/host, land in the app.
  //    (b) Authenticated user navigating manually to `/` on a non-marketing
  //        host (panel, preview) → also bounce to dashboard so `/` never
  //        renders as a blank placeholder on the app host.
  useEffect(() => {
    if (!isReady || !isAuthenticated) return;

    const path = location.pathname;
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const host = typeof window !== "undefined" ? window.location.hostname : "";

    const isFreshLogin = hasFreshLogin() || initialOAuthReturnRef.current;



    // On marketing host: only redirect for fresh logins or explicit OAuth
    // return URLs. A logged-in visitor browsing the landing normally is left
    // alone (Navbar shows "Ir a mi Panel" instead).
    // On app hosts (panel / preview / localhost): also redirect root `/` so
    // the blank Index placeholder never shows.
    const shouldRedirect = shouldRunPostAuthRedirect({
      path,
      hash,
      host,
      hasExplicitLogin: isFreshLogin,
    });

    if (!shouldRedirect) return;

    // Consume the flag so subsequent in-app navigation isn't hijacked.
    clearFreshLogin();
    initialOAuthReturnRef.current = false;

    const intent: PostAuthIntent | null = consumePostAuthIntent();
    const target = intentToPath(intent);

    const url = getPanelUrl(target);
    if (url.startsWith("http")) {
      window.location.replace(url);
    } else {
      navigate(url, { replace: true });
    }
  }, [isReady, isAuthenticated, location.pathname, navigate]);
}
